import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, MessageCircle, Shield, Truck, Award, HeartHandshake, PackageCheck, Building2, MapPin, Store, Users, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Section, Reveal } from "@/components/shared/Section";
import { useSite } from "@/contexts/SiteContext";
import api from "@/lib/api";
import { HOME } from "@/constants/testIds";

export default function Home() {
  const { settings } = useSite();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { featured: true, limit: 6 } }).then((r) => setProducts(r.data.items || [])).catch(() => {}),
      api.get("/brands").then((r) => setBrands(r.data)).catch(() => {}),
      api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}),
      api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {}),
      api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {}),
      api.get("/blogs").then((r) => setBlogs(r.data.slice(0, 3))).catch(() => {}),
      api.get("/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {}),
    ]);
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid={HOME.hero}>
        <div className="absolute inset-0">
          <img src={settings.hero_image} alt="Wholesale garments" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-navy/95 via-navy/75 to-black/85 dark:from-black/90 dark:via-black/75 dark:to-black/95" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 text-white">
            <motion.div
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-brand_blue" />
                <span className="h-2 w-2 rounded-full bg-brand_green" />
                <span className="h-2 w-2 rounded-full bg-gold" />
              </span>
              <p className="section-label text-gold">Enterprise B2B • Est. 2016 • Pan India</p>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="serif-display text-5xl md:text-7xl lg:text-8xl leading-[1.02] text-white"
            >
              {settings.hero_headline?.split(".")[0]}.
              <br />
              <span className="gold-text">{settings.hero_headline?.split(".").slice(1).join(".").trim() || "Delivered Pan-India."}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-7 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed"
            >
              {settings.hero_subheadline}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <Link to="/request-catalogue">
                <Button data-testid={HOME.heroCta} className="rounded-sm h-12 px-7 bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs">
                  Request Catalogue <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/request-quotation">
                <Button data-testid={HOME.heroCtaSecondary} variant="outline" className="rounded-sm h-12 px-7 border-white/30 bg-transparent hover:bg-white/10 text-white tracking-wider uppercase text-xs">
                  Request Quotation
                </Button>
              </Link>
              <a href={`tel:${settings.phone_primary}`} className="inline-flex items-center gap-2 h-12 px-6 rounded-sm border border-white/20 text-white/90 text-xs tracking-wider uppercase hover:border-gold/60 hover:text-gold transition-colors">
                <Phone size={14} /> {settings.phone_primary}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
            >
              {[
                { v: settings.stats_shops, l: "Retailers Served", c: "bg-brand_blue" },
                { v: settings.stats_brands, l: "Premium Brands", c: "bg-brand_green" },
                { v: settings.stats_states, l: "Indian States", c: "bg-gold" },
                { v: settings.stats_years, l: "Years of Trust", c: "bg-white/60" },
              ].map((s, i) => (
                <div key={i} className="border-t border-white/20 pt-4 relative">
                  <span className={`absolute -top-px left-0 h-px w-10 ${s.c}`} />
                  <p className="font-serif text-3xl md:text-4xl text-white leading-none">{s.v}</p>
                  <p className="mt-2 text-[0.65rem] tracking-widest uppercase text-white/60">{s.l}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="hidden lg:block lg:col-span-4"
          >
            <div className="relative glass rounded-md p-7 shadow-2xl">
              <div className="absolute -top-3 -right-3 h-16 w-16 rounded-full bg-gold/20 blur-2xl" />
              <div className="absolute top-0 left-0 h-1 w-full rounded-t-md overflow-hidden flex">
                <span className="flex-1 bg-brand_blue" />
                <span className="flex-1 bg-gold" />
                <span className="flex-1 bg-brand_green" />
              </div>
              <p className="section-label text-gold">Business Enquiry</p>
              <p className="mt-3 font-serif text-2xl text-foreground leading-tight">Start a partnership in 24 hours.</p>
              <ul className="mt-6 space-y-3 text-sm text-foreground/80">
                <li className="flex gap-3"><PackageCheck className="text-brand_blue shrink-0 mt-0.5" size={16} /> Original brands with tags</li>
                <li className="flex gap-3"><Truck className="text-brand_green shrink-0 mt-0.5" size={16} /> Pan India dispatch</li>
                <li className="flex gap-3"><Shield className="text-gold shrink-0 mt-0.5" size={16} /> Transparent B2B pricing</li>
                <li className="flex gap-3"><HeartHandshake className="text-brand_blue shrink-0 mt-0.5" size={16} /> Long-term dealer support</li>
              </ul>
              <Link to="/request-quotation">
                <Button className="mt-7 w-full rounded-sm h-11 bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs">
                  Start Enquiry
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-[0.65rem] tracking-widest uppercase animate-pulse">
          Scroll to explore
        </div>
      </section>

      {/* TRUSTED BY BRANDS - MARQUEE */}
      <section className="border-y border-border/60 py-10 overflow-hidden bg-secondary/20" data-testid="home-brands-marquee">
        <div className="mx-auto max-w-7xl px-4 md:px-8 mb-6 flex items-center justify-between flex-wrap gap-3">
          <p className="section-label">Trusted Brands We Distribute</p>
          <Link to="/brands" className="text-xs tracking-wider uppercase text-foreground/60 hover:text-gold flex items-center gap-2">
            View all brands <ArrowRight size={13} />
          </Link>
        </div>
        <div className="relative">
          <div className="flex gap-14 animate-marquee whitespace-nowrap">
            {[...brands, ...brands, ...brands].slice(0, 30).map((b, i) => (
              <span key={i} className="font-serif text-2xl md:text-3xl text-foreground/50 hover:text-gold transition-colors shrink-0">
                {b.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <Section
        label="Featured Categories"
        title="A wholesale portfolio built for Indian retail."
        subtitle="From premium men's wear to trending ethnic collections — every category tailored for the modern retailer, boutique owner, and distributor."
        testId="home-categories"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.slice(0, 6).map((c, i) => (
            <Reveal key={c.id} delay={i * 0.05}>
              <Link to={`/products?category=${encodeURIComponent(c.name)}`} className="group block relative overflow-hidden rounded-md border border-border/60 hover:border-gold/60 transition-colors">
                <div className="aspect-[4/5] overflow-hidden bg-secondary">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" decoding="async" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <p className="section-label text-gold mb-2">Wholesale Range</p>
                  <h3 className="font-serif text-3xl text-white">{c.name}</h3>
                  <p className="mt-2 text-sm text-white/70 line-clamp-2">{c.description}</p>
                  <div className="mt-4 flex items-center text-white text-xs tracking-widest uppercase">
                    Explore <ArrowRight size={14} className="ml-2 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* WHY CHOOSE */}
      <Section label="Why Businesses Choose Hermanos" title="Enterprise-grade wholesale supply." className="bg-secondary/30">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: PackageCheck, title: "Original Branded Stock", desc: "Authentic tags, original packaging, verified sourcing.", tint: "brand_blue" },
            { icon: Truck, title: "Pan-India Delivery", desc: "Serving 28+ states through reliable logistics partners.", tint: "brand_green" },
            { icon: Shield, title: "Transparent Pricing", desc: "Clear wholesale rates. No hidden charges. Proper billing.", tint: "gold" },
            { icon: HeartHandshake, title: "Dealer Partnership", desc: "Dedicated relationship manager for every business partner.", tint: "brand_blue" },
            { icon: Award, title: "40+ Trusted Brands", desc: "Curated multi-brand catalogue for retail success.", tint: "brand_green" },
            { icon: Building2, title: "Bulk Warehousing", desc: "Ready inventory for large volume dispatches.", tint: "gold" },
            { icon: Sparkles, title: "Seasonal Refresh", desc: "New arrivals every month across all categories.", tint: "brand_blue" },
            { icon: Store, title: "Retailer Ecosystem", desc: "Loved by 10,000+ shops across Bharat.", tint: "brand_green" },
          ].map((f, i) => {
            const tintBg = f.tint === "brand_blue" ? "bg-brand_blue/10 text-brand_blue" : f.tint === "brand_green" ? "bg-brand_green/10 text-brand_green" : "bg-gold/10 text-gold";
            return (
              <Reveal key={i} delay={i * 0.05}>
                <div className="h-full p-6 rounded-md border border-border/60 bg-card hover:border-gold/60 hover:-translate-y-1 transition-all duration-300">
                  <div className={`h-11 w-11 rounded-sm flex items-center justify-center mb-4 ${tintBg}`}>
                    <f.icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl text-foreground">{f.title}</h3>
                  <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* INDUSTRIES / WE SERVE */}
      <Section label="Industries We Serve" title="From boutiques to multi-city distributors." subtitle="We power garment retail across every scale — helping small boutiques and large distributors alike source with confidence.">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { icon: Store, l: "Retail Stores" },
            { icon: Users, l: "Wholesalers" },
            { icon: Building2, l: "Distributors" },
            { icon: Sparkles, l: "Boutiques" },
            { icon: PackageCheck, l: "Resellers" },
            { icon: Award, l: "Fashion Stores" },
          ].map((i, k) => (
            <Reveal key={k} delay={k * 0.05}>
              <div className="p-5 rounded-md border border-border/60 hover:border-gold/60 text-center transition-colors">
                <i.icon className="mx-auto text-gold" size={22} strokeWidth={1.5} />
                <p className="mt-3 text-sm text-foreground/80 tracking-wider uppercase">{i.l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* FEATURED PRODUCTS */}
      <Section label="Featured Catalogue" title="Curated wholesale collections." subtitle="A selection of best-selling styles across brands and categories. Request a full catalogue for pricing and MOQ." className="bg-secondary/30">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.slice(0, 6).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.04}>
              <Link to={`/products/${p.slug}`} className="group block relative overflow-hidden rounded-md border border-border/60 hover:border-gold/60 transition-colors bg-card">
                <div className="aspect-[4/5] overflow-hidden bg-secondary">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground/30 text-xs">No Image</div>
                  )}
                </div>
                <div className="p-5">
                  <p className="section-label text-gold text-[0.65rem]">{p.brand}</p>
                  <h4 className="mt-2 font-serif text-xl text-foreground group-hover:text-gold transition-colors">{p.name}</h4>
                  <div className="mt-3 flex items-center justify-between text-xs text-foreground/60">
                    <span>MOQ: {p.moq} pcs</span>
                    <span className="text-gold group-hover:translate-x-1 transition-transform flex items-center gap-1">
                      Enquire <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/products">
            <Button variant="outline" className="rounded-sm h-11 border-gold/40 hover:border-gold hover:bg-gold/5 tracking-wider uppercase text-xs px-7">
              Explore Full Catalogue <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* BUSINESS PROCESS */}
      <Section label="Wholesale Journey" title="From enquiry to dispatch — clear, simple, professional.">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { n: "01", t: "Enquiry", d: "Share your business details via catalogue or quotation form.", tint: "text-brand_blue/60 border-brand_blue/40" },
            { n: "02", t: "Consultation", d: "Our team helps you build the right assortment for your market.", tint: "text-gold/60 border-gold/40" },
            { n: "03", t: "Confirmation", d: "Approve the order, MOQ, and delivery timeline transparently.", tint: "text-brand_green/60 border-brand_green/40" },
            { n: "04", t: "Dispatch", d: "Bulk pack, proper billing, and pan-India dispatch within days.", tint: "text-gold/60 border-gold/40" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className={`relative pt-8 border-t ${s.tint.split(" ")[1]}`}>
                <span className={`absolute -top-4 left-0 font-serif text-5xl ${s.tint.split(" ")[0]}`}>{s.n}</span>
                <h3 className="font-serif text-2xl text-foreground">{s.t}</h3>
                <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* GALLERY PREVIEW */}
      {gallery.length ? (
        <Section label="Inside Hermanos" title="Warehouse. Showroom. Business floor." subtitle="A glimpse of our operations, packaging, and partner interactions." className="bg-secondary/30">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {gallery.map((g, i) => (
              <Reveal key={g.id} delay={i * 0.04}>
                <div className={`overflow-hidden rounded-md ${i % 4 === 0 ? "row-span-2 aspect-[3/6]" : "aspect-square"}`}>
                  <img src={g.image} alt={g.title} loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/gallery">
              <Button variant="outline" className="rounded-sm h-11 border-gold/40 hover:border-gold hover:bg-gold/5 tracking-wider uppercase text-xs px-7">View Full Gallery</Button>
            </Link>
          </div>
        </Section>
      ) : null}

      {/* TESTIMONIALS */}
      {testimonials.length ? (
        <Section label="Business Testimonials" title="What our partners say.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t, i) => (
              <Reveal key={t.id} delay={i * 0.08}>
                <div className="h-full p-8 rounded-md border border-border/60 bg-card hover:border-gold/60 transition-colors">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating || 5 }).map((_, k) => (
                      <Star key={k} size={14} className="text-gold fill-gold" />
                    ))}
                  </div>
                  <p className="font-serif text-xl leading-relaxed text-foreground">
                    "{t.message}"
                  </p>
                  <div className="mt-6 pt-4 border-t border-border">
                    <p className="text-sm text-foreground font-semibold">{t.name}</p>
                    <p className="text-xs text-foreground/60 mt-0.5">
                      {t.company}{t.city ? ` • ${t.city}` : ""}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      ) : null}

      {/* LATEST BLOGS */}
      {blogs.length ? (
        <Section label="Journal" title="Insights for retailers & distributors." className="bg-secondary/30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blogs.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.08}>
                <Link to={`/blogs/${b.slug}`} className="group block h-full border border-border/60 hover:border-gold/60 rounded-md overflow-hidden bg-card transition-colors">
                  <div className="aspect-[16/10] overflow-hidden bg-secondary">
                    {b.cover_image ? <img src={b.cover_image} alt={b.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" /> : null}
                  </div>
                  <div className="p-6">
                    <p className="section-label mb-3">{new Date(b.publish_date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</p>
                    <h3 className="font-serif text-xl text-foreground group-hover:text-gold transition-colors">{b.title}</h3>
                    <p className="mt-3 text-sm text-foreground/70 line-clamp-2">{b.excerpt}</p>
                    <p className="mt-4 text-xs tracking-wider uppercase text-gold flex items-center gap-1">Read Article <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" /></p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Section>
      ) : null}

      {/* FAQ PREVIEW */}
      {faqs.length ? (
        <Section label="Quick Answers" title="Frequently asked by retail partners.">
          <div className="max-w-4xl">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {faqs.slice(0, 5).map((f) => (
                <AccordionItem key={f.id} value={f.id} className="border border-border/60 rounded-md px-5">
                  <AccordionTrigger className="text-left font-serif text-lg py-5 hover:text-gold">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-foreground/70 pb-5 leading-relaxed">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-8">
              <Link to="/faq" className="text-xs tracking-widest uppercase text-gold hover:underline">View all FAQs →</Link>
            </div>
          </div>
        </Section>
      ) : null}

      {/* CTA */}
      <section className="relative py-24 md:py-32 border-t border-border/60 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-navy via-black to-emerald_brand" />
          <div className="absolute inset-0 grain opacity-40" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 md:px-8 text-center">
          <p className="section-label text-gold mb-5">Ready to grow your retail?</p>
          <h2 className="serif-display text-4xl md:text-6xl lg:text-7xl text-white">
            Let's build a wholesale partnership.
          </h2>
          <p className="mt-6 text-white/70 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            Request our latest catalogue or request a bulk quotation. A dedicated business executive will reach out within 24 hours.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/request-catalogue">
              <Button className="rounded-sm h-12 px-7 bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs">
                Request Catalogue
              </Button>
            </Link>
            <Link to="/request-quotation">
              <Button variant="outline" className="rounded-sm h-12 px-7 border-white/30 hover:bg-white/10 text-white tracking-wider uppercase text-xs">
                Request Quotation
              </Button>
            </Link>
            <a href={`https://wa.me/${settings.whatsapp?.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-12 px-6 rounded-sm border border-white/30 text-white/90 text-xs tracking-wider uppercase hover:border-gold/60 hover:text-gold transition-colors">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
          <div className="mt-10 flex items-center justify-center gap-2 text-white/60 text-xs tracking-wider uppercase">
            <MapPin size={13} /> {settings.address}
          </div>
        </div>
      </section>
    </div>
  );
}
